import { useEffect, useRef } from "react";

interface TurnBasedOrbControllerProps {
  orb: React.MutableRefObject<any>;
  isPlaying?: boolean;
  level?: number; // instantaneous audio level 0..1 (optional)
}

function TurnBasedOrbController({ orb, isPlaying = false, level = 0 }: TurnBasedOrbControllerProps) {
  const baseScaleRef = useRef(0.5); // resting size
  const smoothedScaleRef = useRef(0.5);
  const smoothedLevelRef = useRef(0);
  const isPlayingRef = useRef(isPlaying);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Track latest external level
  const externalLevelRef = useRef(level);
  useEffect(() => {
    externalLevelRef.current = Math.max(0, Math.min(1, level));
  }, [level]);

  // Use requestAnimationFrame to smoothly update the orb's scale every frame.
  useEffect(() => {
    let animationFrameId: number;
    let lastNow = performance.now();

    const animate = () => {
      const now = performance.now();
      const dt = Math.min(100, now - lastNow);
      lastNow = now;

      const base = baseScaleRef.current;
      const amplitude = 0.25; // max growth from base when level=1

      // Smooth the incoming audio level with time scaling
      const riseAlpha = 1 - Math.pow(0.1, dt / 16.67); // fast rise
      const fallAlpha = 1 - Math.pow(0.02, dt / 16.67); // slower fall for natural feel
      const currentLevel = externalLevelRef.current;
      const alpha = currentLevel > smoothedLevelRef.current ? riseAlpha : fallAlpha;
      smoothedLevelRef.current = smoothedLevelRef.current + (currentLevel - smoothedLevelRef.current) * alpha;

      // If not playing, gently decay level to zero
      if (!isPlayingRef.current) {
        const decayAlpha = 1 - Math.pow(0.01, dt / 16.67);
        smoothedLevelRef.current = smoothedLevelRef.current * (1 - decayAlpha);
      }

      // Target scale derived from smoothed level
      const target = base + amplitude * smoothedLevelRef.current;

      // Smooth scale itself to avoid jitter
      const followAlpha = 1 - Math.pow(0.3, dt / 16.67);
      smoothedScaleRef.current = smoothedScaleRef.current + (target - smoothedScaleRef.current) * followAlpha;

      const minScale = base;
      const maxScale = base + amplitude;
      const scale = Math.max(minScale, Math.min(maxScale, smoothedScaleRef.current));

      if (orb.current) {
        orb.current.scale.x = scale;
        orb.current.scale.y = scale;
        orb.current.scale.z = scale;
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [orb]);

  return null;
}

export default TurnBasedOrbController;
